import { useTranslation } from "react-i18next";
import type { User, ThemeClasses } from "../types";
import useIsMonitor from "../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";

interface UserDetailsModalProps {
  user: User;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (user: User) => void;
  onDelete?: (user: User) => void;
  onHardDelete?: (user: User) => void;
  onActivate?: (user: User) => void;
  isAdminReseller?: boolean;
}

export function UserDetailsModal({
  user,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onDelete,
  onHardDelete,
  onActivate,
  isAdminReseller,
}: UserDetailsModalProps) {
  const { t } = useTranslation("adminSections");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "R5tW0uX4yY9cF6dH2aI8":
        return "bg-primary-500/20 text-primary-400 border-primary-500/30";
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "R5tW0uX4yY9cF6dH2aI8":
        return t("users.status.active");
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return t("users.status.inactive");
      default:
        return t("users.status.inactive");
    }
  };

  const formatDate = (timestamp?: { _seconds: number; _nanoseconds: number }) => {
    if (!timestamp) return "No disponible";
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const { isMonitor } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center p-4 sm:p-6 border-b border-gray-700/50 bg-inherit">
          <div className="flex items-center space-x-4">
            <div
              className={`w-14 h-14 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-dark-border" : "bg-gray-100"
                }`}
            >
              <span className={`text-xl font-bold ${themeClasses.text}`}>
                {getInitials(user.first_name, user.last_name)}
              </span>
            </div>
            <div>
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                {user.first_name} {user.last_name}
              </h2>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                @{user.username}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Status & Role */}
          <div className="flex items-center flex-wrap gap-3">
            <span
              className={`text-sm px-3 py-1.5 rounded-full border ${getStatusColor(
                user.status_id
              )}`}
            >
              {getStatusLabel(user.status_id)}
            </span>
            {user.role && (
              <span
                className={`text-sm px-3 py-1.5 rounded-full border ${isDarkMode
                    ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                    : "bg-blue-100 text-blue-700 border-blue-200"
                  }`}
              >
                <i className="ri-shield-user-line mr-1"></i>
                {user.role.name}
              </span>
            )}
          </div>

          {/* Contact Info */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              {t("users.fields.contactInfo")}
            </h3>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } rounded-xl p-4 space-y-3`}
            >
              <div className="flex items-center space-x-3">
                <i className={`ri-mail-line ${themeClasses.textSecondary}`}></i>
                <span className={themeClasses.text}>{user.email}</span>
              </div>
              {user.phone && (
                <div className="flex items-center space-x-3">
                  <i className={`ri-phone-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.text}>{user.phone}</span>
                </div>
              )}
              {user.address && (
                <div className="flex items-center space-x-3">
                  <i className={`ri-map-pin-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.text}>{user.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Company Info */}
          {user.company && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
                {t("users.fields.company")}
              </h3>
              <div
                className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } rounded-xl p-4`}
              >
                <div className="flex items-center space-x-3">
                  <i className={`ri-building-2-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.text}>{user.company.name}</span>
                </div>
              </div>
            </div>
          )}

          {/* Assigned Devices */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              {t("users.fields.assignedUnits")}
              {user.devices && user.devices.length > 0 && (
                <span className={`ml-2 text-xs font-normal ${themeClasses.textSecondary}`}>
                  ({user.devices?.length})
                </span>
              )}
            </h3>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } rounded-xl p-4`}
            >
              {user.devices && user.devices.length > 0 ? (
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {user.devices.map((device) => (
                    <div
                      key={device.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-white"
                        } border ${themeClasses.cardBorder}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-dark-border" : "bg-gray-100"
                            }`}
                        >
                          <i className={`ri-gps-line text-lg ${themeClasses.textSecondary}`}></i>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${themeClasses.text}`}>
                            {device.identifier}
                          </p>
                          {device.name && (
                            <p className={`text-xs ${themeClasses.textSecondary}`}>
                              {device.name}
                              {device.vehicle?.plate && ` - ${device.vehicle.plate}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-4">
                  <i className={`ri-gps-line text-3xl ${themeClasses.textSecondary} mb-2`}></i>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    {t("users.noAssignedUnits")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Account Info */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
              {t("users.fields.accountInfo")}
            </h3>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } rounded-xl p-4 space-y-3`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <i className={`ri-user-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.textSecondary}>{t("users.fields.user")}</span>
                </div>
                <span className={themeClasses.text}>{user.username}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <i className={`ri-calendar-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.textSecondary}>{t("users.fields.created")}</span>
                </div>
                <span className={`text-sm ${themeClasses.text}`}>
                  {formatDate(user.createdAt)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <i className={`ri-refresh-line ${themeClasses.textSecondary}`}></i>
                  <span className={themeClasses.textSecondary}>{t("users.fields.updated")}</span>
                </div>
                <span className={`text-sm ${themeClasses.text}`}>
                  {formatDate(user.updatedAt)}
                </span>
              </div>
              {user.group_id && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className={`ri-team-line ${themeClasses.textSecondary}`}></i>
                    <span className={themeClasses.textSecondary}>{t("users.fields.group")}</span>
                  </div>
                  <span className={themeClasses.text}>{user.group_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-700/50 bg-inherit">
          <button
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium transition-all`}
          >
            {t("users.actions.close")}
          </button>

          {/* Deactivate button - only for active users and admin reseller */}
          {user.status_id === "active" && isAdminReseller && onDelete && (
            <button
              onClick={() => onDelete(user)}
              className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-all"
            >
              {t("users.actions.deactivate")}
            </button>
          )}

          {/* Activate button - only for inactive users and admin reseller */}
          {user.status_id === "S6uX1vY5zZ0dG7eI3bJ9" && isAdminReseller && onActivate && (
            <button
              onClick={() => onActivate(user)}
              className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-xl transition-all"
            >
              {t("users.actions.activate")}
            </button>
          )}

          {/* Delete button - only for inactive users and admin reseller */}
          {user.status_id === "S6uX1vY5zZ0dG7eI3bJ9" && isAdminReseller && onHardDelete && (
            <button
              onClick={() => onHardDelete(user)}
              className="px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-all"
            >
              {t("users.actions.delete")}
            </button>
          )}

          {!isMonitor && (
            <CRUDTooltip>
              <button
                onClick={() => onEdit(user)}
                disabled={disableCRUD}
                className="px-4 py-2.5 bg-primary-500 hover:bg-[var(--brand-primary-hover)] text-gray-900 font-medium rounded-xl transition-all"
              >
                {t("users.actions.editUser")}
              </button>
            </CRUDTooltip>
          )}
        </div>
      </div>
    </div>
  );
}
